/**
 * Sandbox API client.
 *
 * The sandbox endpoints live at `/api/sandbox/` — outside the versioned
 * `/api/v1/` namespace that `apiClient` is anchored to — so we use `fetch`
 * directly with same-origin credentials.
 */
import { ensureCsrfHeader } from '@/utils/csrf';

export interface SandboxProject {
  id: number;
  name: string;
}

const SANDBOX_BASE = '/api/sandbox';

export async function getSandbox(): Promise<SandboxProject | null> {
  const res = await fetch(`${SANDBOX_BASE}/`, {
    method: 'GET',
    credentials: 'same-origin',
    headers: { Accept: 'application/json' },
  });

  if (res.status === 404) return null;
  if (!res.ok) {
    throw new Error(`Failed to fetch sandbox: ${res.status}`);
  }
  return (await res.json()) as SandboxProject;
}

export async function resetSandbox(): Promise<SandboxProject> {
  const headers = await ensureCsrfHeader({
    Accept: 'application/json',
    'Content-Type': 'application/json',
  });

  const res = await fetch(`${SANDBOX_BASE}/reset/`, {
    method: 'POST',
    credentials: 'same-origin',
    headers,
  });

  if (!res.ok) {
    let detail = `Failed to reset sandbox: ${res.status}`;
    try {
      const body = await res.json();
      if (body?.detail) detail = String(body.detail);
    } catch {
      // ignore
    }
    throw new Error(detail);
  }
  return (await res.json()) as SandboxProject;
}
